import { useState } from 'react'
import axios from 'axios'

export function StockAnalysis() {
  const [ticker, setTicker] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState('')
  
  const handleAnalyze = async () => {
    if (!ticker) return
    
    setLoading(true)
    setError('')
    setResult(null)
    
    try {
      const response = await axios.post('https://fd0oqhf3la.execute-api.us-east-1.amazonaws.com/prod/reports/analyze', {
        ticker: ticker.toUpperCase()
      })
      
      setResult(response.data)
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-4">Analyze Stock</h2>
        
        <div className="flex space-x-4 mb-6">
          <input
            type="text"
            value={ticker}
            onChange={(e) => setTicker(e.target.value.toUpperCase())}
            placeholder="Enter ticker (e.g., AAPL)"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
          />
          <button
            onClick={handleAnalyze}
            disabled={loading || !ticker}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Analyzing...' : 'Analyze'}
          </button>
        </div>
        
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Analyzing {ticker}... (takes ~80 seconds)</p>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        
        {result && (
          <div className="bg-white border rounded-lg p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-2xl font-bold">{result.ticker}</h3>
                <p className="text-gray-600">Report ID: {result.report_id}</p>
              </div>
              <div className={`text-right ${
                result.recommendation === 'strong_buy' || result.recommendation === 'buy' ? 'text-green-600' :
                result.recommendation === 'strong_sell' || result.recommendation === 'sell' ? 'text-red-600' :
                'text-yellow-600'
              }`}>
                <div className="text-2xl font-bold uppercase">{result.recommendation?.replace('_', ' ') || 'N/A'}</div>
                <div className="text-sm">Confidence: {Number((result?.confidence ?? 0) * 100).toFixed(0)}%</div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div>
                <div className="text-gray-600 text-sm">Current Price</div>
                <div className="text-2xl font-bold">
                  {typeof result?.current_price === 'number' ? `$${result.current_price.toFixed(2)}` : 'N/A'}
                </div>
              </div>
              {typeof result?.target_price === 'number' && (
                <div>
                  <div className="text-gray-600 text-sm">Target Price</div>
                  <div className="text-2xl font-bold">${result.target_price.toFixed(2)}</div>
                </div>
              )}
              <div>
                <div className="text-gray-600 text-sm">Risk Level</div>
                <div className={`text-xl font-bold ${
                  (result?.risk_level === 'low') ? 'text-green-600' :
                  (result?.risk_level === 'moderate') ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {(result?.risk_level || 'N/A').toString().toUpperCase()}
                </div>
              </div>
            </div>
            
            <div className="pt-4">
              <p className="text-sm text-gray-500">Generated: {result?.generated_at ? new Date(result.generated_at).toLocaleString() : 'N/A'}</p>
              {result.download_url && (
                <a href={result.download_url} target="_blank" rel="noreferrer" className="text-blue-600 underline ml-2">Download Markdown</a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

